import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse, CommonAreaFee, CommonAreaFeePreview } from '@/types'

// Query keys
export const commonAreaFeeKeys = {
  all: ['common_area_fees'] as const,
  lists: () => [...commonAreaFeeKeys.all, 'list'] as const,
  list: (propertyId: number, showAll?: boolean) =>
    [...commonAreaFeeKeys.lists(), propertyId, showAll] as const,
  details: () => [...commonAreaFeeKeys.all, 'detail'] as const,
  detail: (propertyId: number, id: number) =>
    [...commonAreaFeeKeys.details(), propertyId, id] as const,
  previews: () => [...commonAreaFeeKeys.all, 'preview'] as const,
  preview: (propertyId: number, params: PreviewParams) =>
    [...commonAreaFeeKeys.previews(), propertyId, params] as const,
}

export interface PreviewParams {
  fee_type?: string
  calculation_method?: string
  rate?: number
  vat_rate?: number
}

// List fees for a property
export function useCommonAreaFees(propertyId: number, showAll?: boolean) {
  return useQuery({
    queryKey: commonAreaFeeKeys.list(propertyId, showAll),
    queryFn: async () => {
      const params = showAll ? '?all=true' : ''
      const { data } = await api.get<ApiResponse<CommonAreaFee[]>>(
        `/api/properties/${propertyId}/common_area_fees${params}`
      )
      return data.data
    },
    enabled: !!propertyId,
  })
}

// Get single fee
export function useCommonAreaFee(propertyId: number, id: number) {
  return useQuery({
    queryKey: commonAreaFeeKeys.detail(propertyId, id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<CommonAreaFee>>(
        `/api/properties/${propertyId}/common_area_fees/${id}`
      )
      return data.data
    },
    enabled: !!propertyId && !!id,
  })
}

// Preview per-unit breakdown
export function useCommonAreaFeePreview(propertyId: number, params: PreviewParams, enabled: boolean) {
  return useQuery({
    queryKey: commonAreaFeeKeys.preview(propertyId, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params.fee_type) searchParams.set('fee_type', params.fee_type)
      if (params.calculation_method) searchParams.set('calculation_method', params.calculation_method)
      if (params.rate !== undefined) searchParams.set('rate', String(params.rate))
      if (params.vat_rate !== undefined) searchParams.set('vat_rate', String(params.vat_rate))
      const { data } = await api.get<ApiResponse<CommonAreaFeePreview>>(
        `/api/properties/${propertyId}/common_area_fees/preview?${searchParams}`
      )
      return data.data
    },
    enabled: !!propertyId && enabled,
  })
}

// Create fee
export interface CreateCommonAreaFeeInput {
  propertyId: number
  name: string
  fee_type: CommonAreaFee['fee_type']
  calculation_method: CommonAreaFee['calculation_method']
  rate: number
  vat_rate: number
  effective_from: string
  effective_until?: string | null
  description?: string | null
}

export function useCreateCommonAreaFee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, ...input }: CreateCommonAreaFeeInput) => {
      const { data } = await api.post<ApiResponse<CommonAreaFee>>(
        `/api/properties/${propertyId}/common_area_fees`,
        { common_area_fee: input }
      )
      return { data: data.data, propertyId }
    },
    onSuccess: () => {
      removeListQueries(queryClient, commonAreaFeeKeys.lists())
    },
  })
}

// Update fee
export interface UpdateCommonAreaFeeInput {
  id: number
  propertyId: number
  name?: string
  fee_type?: CommonAreaFee['fee_type']
  calculation_method?: CommonAreaFee['calculation_method']
  rate?: number
  vat_rate?: number
  effective_from?: string
  effective_until?: string | null
  description?: string | null
}

export function useUpdateCommonAreaFee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, propertyId, ...input }: UpdateCommonAreaFeeInput) => {
      const { data } = await api.patch<ApiResponse<CommonAreaFee>>(
        `/api/properties/${propertyId}/common_area_fees/${id}`,
        { common_area_fee: input }
      )
      return { data: data.data, propertyId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, commonAreaFeeKeys.list(result.propertyId))
      invalidateDetailQueries(queryClient, commonAreaFeeKeys.detail(result.propertyId, result.data.id))
    },
  })
}

// EP-111 T111-4/T111-8 — "Set new rate" supersede transaction. Only the
// year-to-year variable is accepted (rate/effective_from/vat_rate);
// fee_type/name/calculation_method are carried forward server-side from the
// row being superseded (D8) and are never sent from here.
export interface SetCommonAreaFeeRateInput {
  propertyId: number
  id: number
  rate: number
  effective_from: string
  vat_rate?: number
}

export function useSetCommonAreaFeeRate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, id, ...input }: SetCommonAreaFeeRateInput) => {
      const { data } = await api.post<ApiResponse<CommonAreaFee>>(
        `/api/properties/${propertyId}/common_area_fees/${id}/set_rate`,
        { common_area_fee: input }
      )
      return { data: data.data, propertyId }
    },
    onSuccess: () => {
      // Evicts BOTH the active-only and all=true list caches (list() key is a
      // prefix match) so the CAF rates page's active-rate + history refresh,
      // plus any dependent preview queries keyed off the superseded fee.
      removeListQueries(queryClient, commonAreaFeeKeys.lists())
      removeListQueries(queryClient, commonAreaFeeKeys.previews())
    },
  })
}

// Delete fee — no current UI consumer (EP-116 T028 triage, 2026-08). KEPT,
// not deleted: unlike a hard delete, EP-111's whole design (CommonAreaFee::
// SetRate, the supersede transaction — see the set_rate hook above) exists
// specifically so a rate CHANGE never needs a destructive delete, which
// argues this hook may genuinely have no future use. But the backend
// destroy endpoint is real, unguarded, and recent (EP-111, 2026-07/08) with
// no protective logic ruling out a legitimate "remove a same-day mistake
// before it's ever superseded or invoiced" cleanup action — plausible
// scaffolding for a screen that hasn't shipped yet, not proven dead weight.
// Ambiguous; left documented rather than guessed at either way.
export function useDeleteCommonAreaFee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: number; propertyId: number }) => {
      await api.delete(`/api/properties/${propertyId}/common_area_fees/${id}`)
      return { id, propertyId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, commonAreaFeeKeys.list(result.propertyId))
    },
  })
}
